// Row mappers for the metric registry (migration 20260809100100_metric_registry).
//
// Same convention as boardRows.ts / staffingRows.ts: PostgREST returns
// snake_case, the SPAs read the table directly under RLS, and the mapping
// happens here exactly once so every surface agrees. Nothing here computes.

import {
  isBaseMetricField,
  PROFILE_OUTPUT_NAMES,
  type MetricDef,
  type MetricKind,
  type MetricRounding,
  type MetricUnit,
  type ProfileOutputName,
} from './metricRegistry.js';

export interface MetricRow {
  key: string;
  label_fr: string;
  unit: string;
  kind: string;
  rounding: string;
  source_field: string | null;
  formula: Record<string, unknown> | null;
  source_profile_id: number | null;
  source_output: string | null;
  active: boolean;
  sort: number;
  /** Absent on a client built before the in_summary migration → false. */
  in_summary?: boolean;
}

/** The columns to select — one list, so read paths cannot drift apart. */
export const METRIC_COLUMNS =
  'key,label_fr,unit,kind,rounding,source_field,formula,source_profile_id,source_output,active,sort,in_summary';

const UNITS: readonly MetricUnit[] = ['count', 'personMinutes', 'equivalents', 'heads'];
const KINDS: readonly MetricKind[] = ['base', 'formula', 'profileOutput'];

/**
 * Keep only well-formed key→number pairs.
 *
 * Unlike the legacy `boardRows.formulaFromDb`, keys are NOT filtered against a
 * known list — a registry formula may reference any metric, which is the whole
 * point (ADR-0015 §1). What is still rejected is a non-numeric value: it would
 * poison the sum with NaN, and NaN reads as "no demand", silently hiding a gap.
 */
function formulaFromDb(raw: Record<string, unknown> | null): Record<string, number> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const formula: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'number' && Number.isFinite(value)) formula[key] = value;
  }
  return Object.keys(formula).length === 0 ? null : formula;
}

function unitFromDb(raw: string): MetricUnit {
  return (UNITS as readonly string[]).includes(raw) ? (raw as MetricUnit) : 'count';
}

function kindFromDb(raw: string): MetricKind {
  return (KINDS as readonly string[]).includes(raw) ? (raw as MetricKind) : 'formula';
}

/**
 * Default to 'ceil' on an unrecognised value: it is the historic behaviour of
 * `evaluateFormula`, so an unreadable row degrades to what the board did before
 * the registry existed rather than to a new, unreviewed rounding.
 */
function roundingFromDb(raw: string): MetricRounding {
  return raw === 'none' ? 'none' : 'ceil';
}

function outputFromDb(raw: string | null): ProfileOutputName | null {
  return raw !== null && (PROFILE_OUTPUT_NAMES as readonly string[]).includes(raw)
    ? (raw as ProfileOutputName)
    : null;
}

export function rowToMetric(row: MetricRow): MetricDef {
  return {
    key: row.key,
    labelFr: row.label_fr,
    unit: unitFromDb(row.unit),
    kind: kindFromDb(row.kind),
    rounding: roundingFromDb(row.rounding),
    sourceField: row.source_field !== null && isBaseMetricField(row.source_field) ? row.source_field : null,
    formula: formulaFromDb(row.formula),
    sourceProfileId: row.source_profile_id,
    sourceOutput: outputFromDb(row.source_output),
    active: row.active,
    sort: row.sort,
    inSummary: row.in_summary ?? false,
  };
}

export function rowsToMetrics(rows: readonly MetricRow[]): MetricDef[] {
  return rows.map(rowToMetric);
}

/** The editable fields of a metric. `key` and `kind` are creation-time only. */
export interface MetricUpsert {
  key: string;
  labelFr: string;
  unit: MetricUnit;
  kind: MetricKind;
  rounding: MetricRounding;
  sourceField: string | null;
  formula: Record<string, number> | null;
  active: boolean;
  sort: number;
  inSummary: boolean;
}

export function metricUpsertToRow(input: MetricUpsert): Record<string, unknown> {
  return {
    key: input.key,
    label_fr: input.labelFr,
    unit: input.unit,
    kind: input.kind,
    rounding: input.rounding,
    source_field: input.sourceField,
    formula: input.formula,
    active: input.active,
    sort: input.sort,
    in_summary: input.inSummary,
    updated_at: new Date().toISOString(),
  };
}

/**
 * A metric the operator may edit or delete from the registry editor.
 *
 * `base` rows are code-anchored (the evaluator has to know how to read a stored
 * field) and `profileOutput` rows are trigger-maintained, so both are label-only
 * — deleting either would break a row nothing can recreate from the UI.
 */
export function isOperatorEditable(def: MetricDef): boolean {
  return def.kind === 'formula';
}

/**
 * Client-side guard mirroring the SQL. The database is authoritative — this
 * spares a round trip and gives a French message. Null = valid.
 */
export function metricDraftError(
  input: MetricUpsert,
  existingKeys: readonly string[],
  isNew: boolean,
): string | null {
  const key = input.key.trim();
  if (key === '') return 'La clé est requise.';
  // camelCase identifier: the key is referenced by value inside every formula
  // that uses it, so it must survive JSON round trips without quoting games.
  if (!/^[a-z][A-Za-z0-9]*$/.test(key)) return 'La clé doit être en camelCase (lettres et chiffres, sans accent).';
  if (isNew && existingKeys.includes(key)) return 'Cette clé existe déjà.';
  if (input.labelFr.trim() === '') return 'Le libellé est requis.';

  if (input.kind === 'formula') {
    const terms = Object.entries(input.formula ?? {});
    if (terms.length === 0) return 'Choisissez au moins une métrique.';
    for (const [ref, coefficient] of terms) {
      if (!Number.isFinite(coefficient)) return 'Les coefficients doivent être des nombres.';
      if (ref === key) return 'Une métrique ne peut pas se référencer elle-même.';
      if (!existingKeys.includes(ref)) return `La métrique « ${ref} » n’existe pas.`;
    }
  }
  return null;
}

/** Parse a coefficient, tolerating the French decimal comma. */
export function parseCoefficient(raw: string | undefined): number | null {
  const value = (raw ?? '').trim().replace(',', '.');
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
}
